import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  ArrowLeft, Sparkles, Download, Eye, FileText, TrendingUp, CheckCircle2,
  XCircle, CircleDot, Plus, Loader2, Mail, Copy, Check, ExternalLink, ChevronDown,
  Activity, Clock, DollarSign, Upload, Wand2, ArrowRight, Table2,
  ShieldCheck, ShieldAlert, Scissors,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ScoreRing, KeywordBreakdownCard, scoreColor } from "../components/resumeScore";
import { computeLineDiff, computeLineSimilarity, cleanLine } from "../lib/resumeDiff";
import DocxPreview from "../components/DocxPreview";
import { engineLabel } from "../lib/models";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function friendlyModel(m) {
  return engineLabel(m);
}

/* ── Precise redline diff (inline) ── */
function LineDiffView({ lineDiffs }) {
  const hasChange = lineDiffs?.some((l) => l.type !== "unchanged");
  if (!hasChange) return <div className="text-sm text-muted-foreground p-6 text-center" data-testid="diff-empty">No textual changes were needed — your content already aligned well.</div>;
  return (
    <div className="font-mono text-[0.8rem] leading-relaxed" data-testid="line-diff-container">
      {lineDiffs.map((line, i) => {
        if (line.type === "unchanged") {
          if (!line.text?.trim()) return null;
          return <div key={i} className="px-3 py-1 text-muted-foreground/80">{cleanLine(line.text)}</div>;
        }
        if (line.type === "added") {
          return <div key={i} className="px-3 py-1 bg-emerald-50 dark:bg-emerald-950/20 border-l-2 border-emerald-500 text-emerald-800 dark:text-emerald-300"><span className="select-none mr-1 font-bold">+</span>{cleanLine(line.text)}</div>;
        }
        if (line.type === "removed") {
          return <div key={i} className="px-3 py-1 bg-rose-50 dark:bg-rose-950/20 border-l-2 border-rose-400"><span className="select-none mr-1 font-bold text-rose-500">−</span><span className="line-through text-rose-700/70 dark:text-rose-400/70">{cleanLine(line.text)}</span></div>;
        }
        // modified — precise inline: struck old part + green new part adjacent
        const sim = computeLineSimilarity(line.origLine, line.newLine);
        if (sim < 0.4) {
          return (
            <div key={i} className="my-0.5" data-testid="diff-replaced-block">
              <div className="px-3 py-1 bg-rose-50 dark:bg-rose-950/20 border-l-2 border-rose-400"><span className="select-none mr-1 font-bold text-rose-500">−</span><span className="line-through text-rose-700/70 dark:text-rose-400/70">{cleanLine(line.origLine)}</span></div>
              <div className="px-3 py-1 bg-emerald-50 dark:bg-emerald-950/20 border-l-2 border-emerald-500 text-emerald-800 dark:text-emerald-300"><span className="select-none mr-1 font-bold">+</span>{cleanLine(line.newLine)}</div>
            </div>
          );
        }
        return (
          <div key={i} className="px-3 py-1 bg-muted/30 border-l-2 border-amber-400" data-testid="diff-inline-block">
            {line.wordDiffs.map(([op, text], j) =>
              op === 0 ? <span key={j}>{cleanLine(text)}</span>
              : op === -1 ? <span key={j} className="line-through text-rose-600/70 dark:text-rose-400/60 bg-rose-100/60 dark:bg-rose-950/30 rounded-sm px-0.5">{text}</span>
              : <span key={j} className="text-emerald-700 dark:text-emerald-300 bg-emerald-100/70 dark:bg-emerald-950/30 rounded-sm px-0.5 font-medium">{text}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SideBySide({ original, optimized }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border rounded-xl overflow-hidden border border-border" data-testid="side-by-side">
      <div className="bg-card">
        <div className="px-4 py-2.5 border-b border-border/60 flex items-center justify-between bg-muted/30">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Original</span>
          <span className="text-[0.6rem] font-mono px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400">Before</span>
        </div>
        <pre className="whitespace-pre-wrap break-words text-[0.78rem] leading-relaxed p-4 text-foreground/90 font-sans max-h-[55vh] overflow-y-auto" data-testid="original-text">{original}</pre>
      </div>
      <div className="bg-card">
        <div className="px-4 py-2.5 border-b border-border/60 flex items-center justify-between bg-muted/30">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Optimized</span>
          <span className="text-[0.6rem] font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">After</span>
        </div>
        <pre className="whitespace-pre-wrap break-words text-[0.78rem] leading-relaxed p-4 text-foreground/90 font-sans max-h-[55vh] overflow-y-auto" data-testid="optimized-text">{optimized}</pre>
      </div>
    </div>
  );
}

/* ── Token usage (collapsible, Tailwind) ── */
function TokenUsagePanel({ usageLog }) {
  if (!usageLog || !usageLog.calls?.length) return null;
  const fmt = (n) => (n ?? 0).toLocaleString();
  const cost = (c) => `$${(c || 0).toFixed(5)}`;
  const fStep = (raw) => raw.replace(/\[.*\]$/, "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <details className="bg-card border border-border/50 rounded-xl overflow-hidden rz-fade-up rz-d3" data-testid="token-usage-panel">
      <summary className="cursor-pointer px-5 py-4 flex items-center gap-2.5 select-none list-none">
        <Activity size={16} className="text-foreground" />
        <span className="text-sm font-semibold text-foreground">Token Usage & Cost</span>
        <span className="ml-auto text-xs font-mono px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400" data-testid="token-usage-total-cost">{cost(usageLog.total_cost_usd)}</span>
        <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-muted text-muted-foreground" data-testid="token-usage-total-tokens">{fmt(usageLog.total_tokens)} tok</span>
        <ChevronDown size={15} className="text-muted-foreground" />
      </summary>
      <div className="px-5 pb-5 border-t border-border/50">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 my-4">
          {[
            { l: "Input", v: fmt(usageLog.total_input_tokens), I: Upload },
            { l: "Output", v: fmt(usageLog.total_output_tokens), I: Download },
            { l: "Total", v: fmt(usageLog.total_tokens), I: Activity },
            { l: "Cost", v: cost(usageLog.total_cost_usd), I: DollarSign },
            { l: "Time", v: `${(usageLog.total_time_s || 0).toFixed(1)}s`, I: Clock },
          ].map(({ l, v, I }) => (
            <div key={l} className="rounded-lg border border-border/60 p-2.5 text-center bg-muted/20" data-testid={`token-stat-${l.toLowerCase()}`}>
              <I size={13} className="mx-auto mb-1 text-muted-foreground" />
              <div className="text-sm font-bold text-foreground">{v}</div>
              <div className="text-[0.62rem] uppercase tracking-wide text-muted-foreground">{l}</div>
            </div>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="token-usage-table">
            <thead><tr className="border-b border-border text-muted-foreground">
              {["Step", "Model", "In", "Out", "Cost"].map((h) => <th key={h} className={`py-1.5 px-2 font-medium uppercase text-[0.62rem] ${h === "Step" || h === "Model" ? "text-left" : "text-right"}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {usageLog.calls.map((c, i) => (
                <tr key={i} className="border-b border-border/40" data-testid={`token-row-${i}`}>
                  <td className="py-1.5 px-2 text-foreground">{fStep(c.step)}</td>
                  <td className="py-1.5 px-2"><span className="text-[0.65rem] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{friendlyModel(c.model)}</span></td>
                  <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">{fmt(c.input_tokens)}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">{fmt(c.output_tokens)}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-foreground">{cost(c.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </details>
  );
}

/* ── Recruiter Email (logic preserved, restyled) ── */
function RecruiterEmailDisplay({ result }) {
  const [emailBody, setEmailBody] = useState(result.email_body || "");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [copied, setCopied] = useState(false);
  const [subject, setSubject] = useState("");

  useEffect(() => {
    let body = result.email_body || "";
    let extractedSubject = "";
    const lines = body.split("\n");
    if (lines[0] && lines[0].trim().toLowerCase().startsWith("subject:")) {
      extractedSubject = lines[0].replace(/^subject:\s*/i, "").trim();
      body = lines.slice(1).join("\n").replace(/^\n+/, "");
    }
    body = body.replace(/\*\*(.+?)\*\*/g, "$1");
    setEmailBody(body);
    if (result.job_description) {
      const m = result.job_description.match(/[\w.-]+@[\w.-]+\.\w+/);
      if (m) setRecipientEmail(m[0]);
    }
    if (extractedSubject) setSubject(extractedSubject);
    else {
      let role = result.job_title || "the open position";
      const loc = result.job_location || "";
      if (loc) setSubject(`Application for ${role} – ${loc}`);
      else if (result.company_name) setSubject(`Application for ${role} – ${result.company_name}`);
      else setSubject(`Application for ${role}`);
    }
  }, [result]);

  const handleCopy = () => { navigator.clipboard.writeText(emailBody); setCopied(true); toast.success("Copied!"); setTimeout(() => setCopied(false), 2000); };
  const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(recipientEmail)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;

  return (
    <div className="bg-card border border-border/50 rounded-xl overflow-hidden" data-testid="email-display-card">
      <div className="px-5 py-3.5 border-b border-border/50 flex items-center justify-between">
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2"><Mail size={16} /> Recruiter Email</h3>
        <button onClick={handleCopy} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted transition-colors" data-testid="copy-email-btn">{copied ? <Check size={14} /> : <Copy size={14} />}</button>
      </div>
      <div className="p-5 flex flex-col gap-3">
        <div className="flex items-center gap-3"><label className="text-xs font-mono uppercase tracking-wider text-muted-foreground w-16">To</label><input className="flex-1 px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="recruiter@company.com" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} data-testid="recipient-email-input" /></div>
        <div className="flex items-center gap-3"><label className="text-xs font-mono uppercase tracking-wider text-muted-foreground w-16">Subject</label><input className="flex-1 px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject-input" /></div>
        <textarea className="w-full min-h-[260px] px-3 py-2.5 text-sm rounded-lg border border-border bg-muted/20 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 leading-relaxed" value={emailBody} onChange={(e) => setEmailBody(e.target.value)} data-testid="email-body-textarea" />
        <a href={gmailUrl} target="_blank" rel="noopener noreferrer" className="self-start inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-all" data-testid="open-gmail-btn"><ExternalLink size={14} /> Open in Gmail</a>
      </div>
    </div>
  );
}

function CoverLetterDisplay({ result }) {
  const [copied, setCopied] = useState(false);
  const letterText = result.cover_letter || "";
  const handleCopy = () => { navigator.clipboard.writeText(letterText); setCopied(true); toast.success("Cover letter copied!"); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="bg-card border border-border/50 rounded-xl overflow-hidden" data-testid="cover-letter-display">
      <div className="px-5 py-3.5 border-b border-border/50 flex items-center justify-between">
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2"><FileText size={16} /> Cover Letter</h3>
        <button onClick={handleCopy} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted transition-colors" data-testid="copy-cover-letter-btn">{copied ? <Check size={14} /> : <Copy size={14} />}</button>
      </div>
      <div className="p-6 sm:p-8 max-w-[760px] mx-auto" data-testid="cover-letter-content">
        {letterText.split("\n").map((line, i) => (
          line.trim() === "" ? <div key={i} className="h-3" /> : <p key={i} className="text-sm leading-relaxed text-foreground/90 mb-1.5">{line}</p>
        ))}
      </div>
    </div>
  );
}

const REPORT_STATUS = {
  "woven+skills_section": { label: "Woven into your experience + added to skills", cls: "text-emerald-600 dark:text-emerald-400", Icon: CheckCircle2 },
  woven: { label: "Woven naturally into your experience", cls: "text-emerald-600 dark:text-emerald-400", Icon: CheckCircle2 },
  skills_section: { label: "Added to your skills section", cls: "text-sky-600 dark:text-sky-400", Icon: Table2 },
  section_skipped: { label: "Kept off to protect your resume's accuracy", cls: "text-amber-600 dark:text-amber-400", Icon: ShieldAlert },
  failed: { label: "Could not be placed — add it below", cls: "text-rose-600 dark:text-rose-400", Icon: XCircle },
};

const PLACED_STATUSES = ["woven", "woven+skills_section", "skills_section"];

function SkillsReportCard({ report }) {
  if (!report || report.length === 0) return null;
  const placed = report.filter((r) => PLACED_STATUSES.includes(r.status)).length;
  return (
    <div className="bg-card border border-border/50 rounded-xl overflow-hidden shadow-sm" data-testid="skills-report-card">
      <div className="px-5 py-4 border-b border-border/50 flex items-center justify-between">
        <h3 className="text-base font-semibold tracking-tight text-foreground flex items-center gap-2">
          <ShieldCheck size={17} className="text-emerald-600 dark:text-emerald-400" /> Your approved skills — verified
        </h3>
        <span className="text-xs font-mono text-muted-foreground">{placed}/{report.length} placed</span>
      </div>
      <div className="divide-y divide-border/30">
        {report.map((r, i) => {
          const s = REPORT_STATUS[r.status] || REPORT_STATUS.failed;
          const SIcon = s.Icon;
          return (
            <div key={i} className="px-5 py-2.5 flex items-start gap-2.5" data-testid={`skill-report-${r.skill}`}>
              <SIcon size={15} className={`${s.cls} mt-0.5 shrink-0`} />
              <div className="min-w-0">
                <span className="text-sm font-medium text-foreground flex items-center gap-1.5">
                  {r.skill}
                  {r.forced && <span className="text-[0.6rem] font-mono uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400" data-testid={`forced-chip-${r.skill}`}>Not directly relevant</span>}
                </span>
                <span className={`block text-[0.72rem] ${s.cls}`}>{s.label}</span>
                {r.reason && <span className="block text-[0.7rem] text-muted-foreground mt-0.5">{r.reason}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CollapsibleSkillGroup({ title, hint, items, testid, tone, selected, onToggle, adding }) {
  const [open, setOpen] = useState(false);
  if (!items || items.length === 0) return null;
  const toneCls = tone === "red"
    ? "text-rose-600 dark:text-rose-400"
    : "text-muted-foreground";
  return (
    <div className="border border-border/60 rounded-lg overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-muted/30 transition-colors" data-testid={`${testid}-toggle`}>
        <span className={`text-xs font-semibold ${toneCls} flex items-center gap-1.5`}>
          {tone === "red" ? <ShieldAlert size={13} /> : <CircleDot size={13} />} {title} · {items.length}
        </span>
        <ChevronDown size={14} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 flex flex-col gap-1.5">
          <p className="text-[0.7rem] text-muted-foreground mb-1">{hint}</p>
          {items.map((it, idx) => {
            const checked = selected.has(it.skill);
            return (
              <button key={idx} onClick={() => onToggle(it.skill)} disabled={adding} data-testid={`${testid}-item-${idx}`}
                className={`text-left border rounded-lg px-3 py-2 transition-all flex items-center gap-2.5 ${checked ? "border-primary bg-primary/5" : "border-border/60 hover:border-primary/40 bg-background"}`}>
                <span className={`w-4 h-4 rounded shrink-0 flex items-center justify-center border ${checked ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>{checked && <Check size={11} className="text-primary-foreground" />}</span>
                <span className="text-xs font-medium text-foreground flex-1">{it.skill}</span>
                <span className="text-[0.65rem] text-muted-foreground shrink-0">{it.importance === "required" ? "Required" : "Preferred"}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


export default function ResultsStep({ result, onBack, onUpdateResult }) {
  const [localResult, setLocalResult] = useState(result);
  const [diffMode, setDiffMode] = useState("redline");
  const [showPreview, setShowPreview] = useState(false);
  const [highlightKeywords, setHighlightKeywords] = useState(result?.bold_keywords_on !== false);
  const [selected, setSelected] = useState(new Set());
  const [adding, setAdding] = useState(false);
  const [tracked, setTracked] = useState(false);

  useEffect(() => { setLocalResult(result); }, [result]);

  const before = localResult.cost_info?.before_breakdown;
  const after = localResult.cost_info?.after_breakdown;
  const afterScore = after?.match_score ?? localResult.ats_score?.overall_score ?? 0;
  const beforeScore = before?.match_score ?? null;
  const delta = beforeScore != null ? afterScore - beforeScore : null;
  const modelUsed = friendlyModel(localResult.cost_info?.model_used || localResult.ats_score?.model || "");

  const skillsReport = localResult.cost_info?.skills_report || [];
  const missingGroups = useMemo(() => {
    const approved = new Set((localResult.cost_info?.approved_skills || []).map((s) => String(s).toLowerCase()));
    const notCredible = new Set((localResult.cost_info?.not_credible_skills || []).map((s) => String(s).toLowerCase()));
    const out = { failed: [], skipped: [], notCredible: [] };
    if (!after?.groups) return out;
    Object.values(after.groups).forEach((g) => g.forEach((it) => {
      if (it.state !== "missing") return;
      const k = (it.skill || "").toLowerCase();
      if (approved.has(k)) out.failed.push(it);
      else if (notCredible.has(k)) out.notCredible.push(it);
      else out.skipped.push(it);
    }));
    return out;
  }, [after, localResult.cost_info]);
  const missingItems = [...missingGroups.failed, ...missingGroups.skipped, ...missingGroups.notCredible];

  const lineDiffs = useMemo(() => computeLineDiff(localResult.original_text, localResult.optimized_text), [localResult.original_text, localResult.optimized_text]);

  const handleToggleBold = async () => {
    const v = !highlightKeywords; setHighlightKeywords(v);
    try { await axios.patch(`${API}/optimizations/${localResult.id}/bold-preference`, { bold_keywords_on: v }); } catch {}
  };

  const downloadUrl = `${API}/download/${localResult.id}${highlightKeywords ? "?highlight_keywords=true" : ""}${localResult._rename ? `${highlightKeywords ? "&" : "?"}name=${encodeURIComponent(localResult._rename)}` : ""}`;

  const handleDownload = async () => {
    if (!tracked) {
      try {
        await axios.post(`${API}/applications/auto-track`, {
          optimization_id: localResult.id, job_title: localResult.job_title, company_name: localResult.company_name,
          url: localResult.job_url || "", location: localResult.job_location || "", recruiter_email: localResult.recruiter_email || "", platform: localResult.platform || "",
        });
        setTracked(true); toast.success("Saved to Job Tracker!");
      } catch {}
    }
    window.open(downloadUrl, "_blank");
  };

  const toggleSel = (skill) => setSelected((p) => { const n = new Set(p); n.has(skill) ? n.delete(skill) : n.add(skill); return n; });

  const pollKw = (jobId) => new Promise((resolve, reject) => {
    const start = Date.now();
    const poll = async () => {
      if (Date.now() - start > 300000) { reject(new Error("Timed out.")); return; }
      try {
        const r = await axios.get(`${API}/add-keywords/status/${jobId}`, { timeout: 10000 });
        if (r.data.status === "completed" && r.data.result) resolve(r.data.result);
        else if (r.data.status === "failed") reject(new Error(r.data.error || "Failed."));
        else setTimeout(poll, 3000);
      } catch { setTimeout(poll, 3000); }
    };
    poll();
  });

  const handleAddSelected = async () => {
    const kws = Array.from(selected);
    if (!kws.length) { toast.error("Select at least one skill to add"); return; }
    setAdding(true);
    try {
      const sub = await axios.post(`${API}/add-keywords`, { optimization_id: localResult.id, keywords: kws }, { timeout: 15000 });
      if (sub.data.status === "rejected") throw new Error(sub.data.message);
      (sub.data.warnings || []).forEach((w) => toast.info(w, { duration: 5000 }));
      const res = await pollKw(sub.data.job_id);
      setLocalResult((prev) => ({
        ...prev,
        optimized_text: res.optimized_text,
        ats_score: res.ats_score,
        cost_info: { ...prev.cost_info, after_breakdown: res.after_breakdown || prev.cost_info?.after_breakdown },
      }));
      if (onUpdateResult) onUpdateResult(res);
      setSelected(new Set());
      const cost = res.usage?.total_cost_usd;
      toast.success(`Re-optimized with ${modelUsed}. ${cost ? `Cost: $${cost.toFixed(4)}` : ""}`);
    } catch (e) { toast.error(e.message || "Could not add skills."); } finally { setAdding(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background overflow-y-auto flex flex-col" data-testid="results-step">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/40 h-16 px-4 lg:px-6 flex items-center justify-between shrink-0">
        <button onClick={onBack} className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid="results-back-btn">
          <ArrowLeft size={16} /> <span className="hidden sm:inline">New optimization</span>
        </button>
        <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-foreground/70 min-w-0">
          <FileText size={13} className="shrink-0" />
          <span className="truncate max-w-[40vw]">{localResult.original_filename}{localResult.company_name ? ` → ${localResult.company_name}` : ""}</span>
        </div>
        <span className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-blue-600 dark:text-blue-400" data-testid="results-engine-badge">
          <Sparkles size={13} /> {modelUsed || "AI"}
        </span>
      </header>

      <div className="max-w-7xl mx-auto w-full px-4 lg:px-6 py-8 pb-36 flex flex-col gap-8">
        {/* Score hero: Before → After */}
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 lg:gap-12 items-center p-6 lg:p-8 bg-muted/20 rounded-2xl border border-border/50 rz-fade-up" data-testid="results-score-hero">
          <div className="flex items-center gap-6 justify-center lg:justify-start">
            <ScoreRing value={afterScore} label="Match" />
            <div className="flex flex-col gap-2">
              <div className="text-[0.65rem] font-mono uppercase tracking-[0.2em] text-muted-foreground">JD Match Score</div>
              <div className="flex flex-col gap-1 text-sm">
                <span className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400"><CheckCircle2 size={14} /> {after?.matched_count || 0} matched</span>
                <span className="flex items-center gap-2 text-amber-600 dark:text-amber-400"><CircleDot size={14} /> {after?.partial_count || 0} partial</span>
                <span className="flex items-center gap-2 text-rose-600 dark:text-rose-400"><XCircle size={14} /> {after?.missing_count || 0} missing</span>
              </div>
              <div className="text-xs font-mono text-muted-foreground mt-1">{after?.total_earned || 0}/{after?.total_max || 0} pts</div>
            </div>
          </div>
          <div className="flex flex-col justify-center gap-3 lg:border-l lg:border-border/60 lg:pl-12">
            <div className="text-[0.65rem] font-mono uppercase tracking-[0.2em] text-muted-foreground">Improvement from your accepted changes</div>
            {beforeScore != null ? (
              <>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-black tracking-tighter" style={{ color: scoreColor(beforeScore) }}>{beforeScore}%</div>
                    <div className="text-[0.62rem] font-mono uppercase text-muted-foreground">Before</div>
                  </div>
                  <ArrowRight size={22} className="text-muted-foreground" />
                  <div className="text-center">
                    <div className="text-3xl font-black tracking-tighter" style={{ color: scoreColor(afterScore) }}>{afterScore}%</div>
                    <div className="text-[0.62rem] font-mono uppercase text-muted-foreground">After</div>
                  </div>
                  {delta != null && (
                    <div className={`ml-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold ${delta > 0 ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400" : "bg-muted text-muted-foreground"}`} data-testid="score-delta-badge">
                      <TrendingUp size={15} /> {delta > 0 ? `+${delta}` : delta} pts
                    </div>
                  )}
                </div>
                <div className="h-2 w-full bg-border rounded-full overflow-hidden relative">
                  <div className="absolute inset-y-0 left-0 bg-foreground/20 rounded-full" style={{ width: `${beforeScore}%` }} />
                  <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${afterScore}%`, background: scoreColor(afterScore), transition: "width 1s ease-out" }} />
                </div>
              </>
            ) : (
              <div className="text-2xl font-black tracking-tighter" style={{ color: scoreColor(afterScore) }}>{afterScore}% match</div>
            )}
            <div className="text-xs text-muted-foreground">{localResult.ats_score?.summary || "Scored with the same AI engine used for analysis."}</div>
          </div>
        </div>

        {/* Two balanced columns: keyword breakdown + improve further */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start rz-fade-up rz-d1">
          <div className="flex flex-col gap-6">
            <KeywordBreakdownCard breakdown={after || before} title="Keyword Coverage (after optimization)" testid="results-keyword-breakdown" />
          </div>

          <div className="flex flex-col gap-4">
            <SkillsReportCard report={skillsReport} />
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold tracking-tight text-foreground flex items-center gap-2"><Wand2 size={18} /> Improve further</h3>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              Anything red below was left out <span className="font-medium text-foreground">on purpose</span> — either you didn't approve it or it wasn't credible. Tick skills to add them now (re-runs the AI and uses tokens).
            </p>

            {missingItems.length === 0 ? (
              <div className="rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/20 p-5 flex items-center gap-3 text-sm text-emerald-700 dark:text-emerald-400" data-testid="all-covered-notice">
                <CheckCircle2 size={18} /> All JD skills are covered. You're ready to download.
              </div>
            ) : (
              <>
                {missingGroups.failed.length > 0 && (
                  <div className="flex flex-col gap-2" data-testid="improve-missing-list">
                    <p className="text-xs font-semibold text-rose-600 dark:text-rose-400">Approved but not placed — fix these:</p>
                    {missingGroups.failed.map((it, idx) => {
                      const checked = selected.has(it.skill);
                      return (
                        <button key={idx} onClick={() => toggleSel(it.skill)} disabled={adding} data-testid={`missing-skill-${idx}`}
                          className={`text-left border rounded-lg px-4 py-3 transition-all active:scale-[0.99] flex items-center gap-3 ${checked ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-border hover:border-primary/50 bg-background"}`}>
                          <span className={`w-5 h-5 rounded-md shrink-0 flex items-center justify-center border ${checked ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>{checked && <Check size={13} className="text-primary-foreground" />}</span>
                          <span className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-foreground">{it.skill}</span>
                            <span className="block text-[0.7rem] text-muted-foreground">{it.importance === "required" ? "Required by JD" : "Preferred"} · worth {it.max} pts</span>
                          </span>
                          <XCircle size={15} className="text-rose-500 shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                )}
                <CollapsibleSkillGroup
                  title="Skipped — you chose not to add these" testid="skipped-skills-group"
                  hint="You didn't approve these during analysis, so we left them untouched. Tick any to add now."
                  items={missingGroups.skipped} selected={selected} onToggle={toggleSel} adding={adding} />
                <CollapsibleSkillGroup
                  title="Not credible for this resume — we advise against" testid="not-credible-skills-group" tone="red"
                  hint="Weaving these into your experience would look fabricated to a recruiter. If you add them anyway, they go in at your own risk."
                  items={missingGroups.notCredible} selected={selected} onToggle={toggleSel} adding={adding} />
                {(missingGroups.failed.length > 0 || selected.size > 0) && (
                  <button onClick={handleAddSelected} disabled={adding || selected.size === 0} data-testid="add-selected-skills-btn"
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50">
                    {adding ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                    {adding ? "Re-optimizing…" : `Add ${selected.size || ""} & re-score`}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Tabs: Changes / Email / Cover Letter */}
        <Tabs defaultValue="changes" className="w-full rz-fade-up rz-d2" data-testid="results-tabs">
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="changes" data-testid="tab-changes" className="flex-1 sm:flex-none">Changes</TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email" className="flex-1 sm:flex-none">Recruiter Email</TabsTrigger>
            <TabsTrigger value="cover" data-testid="tab-cover" className="flex-1 sm:flex-none">Cover Letter</TabsTrigger>
          </TabsList>

          <TabsContent value="changes" className="mt-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="inline-flex rounded-lg border border-border p-1 bg-muted/30">
                {[["redline", "Redline"], ["side", "Side by side"], ["final", "Final"]].map(([k, label]) => (
                  <button key={k} onClick={() => setDiffMode(k)} data-testid={`diff-mode-${k}`}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${diffMode === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{label}</button>
                ))}
              </div>
              <div className="flex items-center gap-4 text-[0.7rem] font-mono text-muted-foreground">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-rose-400 inline-block" /> removed</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" /> added</span>
              </div>
            </div>
            <div className="bg-card border border-border/50 rounded-xl overflow-hidden">
              {diffMode === "side" ? <SideBySide original={localResult.original_text} optimized={localResult.optimized_text} />
                : diffMode === "final" ? <pre className="whitespace-pre-wrap break-words text-[0.8rem] leading-relaxed p-5 text-foreground/90 font-sans max-h-[60vh] overflow-y-auto" data-testid="final-text">{localResult.optimized_text}</pre>
                : <div className="py-3 max-h-[60vh] overflow-y-auto"><LineDiffView lineDiffs={lineDiffs} /></div>}
            </div>
          </TabsContent>

          <TabsContent value="email" className="mt-4">
            {localResult.email_body ? <RecruiterEmailDisplay result={localResult} /> : (
              <div className="bg-card border border-border/50 rounded-xl p-6 flex items-center gap-3 text-sm text-muted-foreground" data-testid="no-email-notice">
                <Mail size={16} /> Enable "Generate Recruiter Email" before optimizing to create one alongside your resume.
              </div>
            )}
          </TabsContent>

          <TabsContent value="cover" className="mt-4">
            {localResult.cover_letter ? <CoverLetterDisplay result={localResult} /> : (
              <div className="bg-card border border-border/50 rounded-xl p-6 flex items-center gap-3 text-sm text-muted-foreground" data-testid="no-cover-notice">
                <FileText size={16} /> Enable "Generate Cover Letter" before optimizing to create one here.
              </div>
            )}
          </TabsContent>
        </Tabs>

        {Array.isArray(localResult.cost_info?.hygiene_report) && localResult.cost_info.hygiene_report.length > 0 && (
          <div className="mt-6 bg-card border border-border/50 rounded-xl p-5" data-testid="hygiene-report-card">
            <h3 className="text-base font-semibold tracking-tight text-foreground flex items-center gap-2 mb-1">
              <Sparkles size={16} className="text-primary" /> Resume Cleanup
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              {localResult.cost_info.hygiene_report.length} tidy-up{localResult.cost_info.hygiene_report.length === 1 ? "" : "s"} applied to your skills table (turn off "Resume Hygiene" before optimizing to skip this).
            </p>
            <ul className="flex flex-col gap-1.5" data-testid="hygiene-report-list">
              {localResult.cost_info.hygiene_report.map((line, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground/90">
                  <CheckCircle2 size={15} className="text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {Array.isArray(localResult.cost_info?.prune_report) && localResult.cost_info.prune_report.length > 0 && (
          <div className="mt-6 bg-card border border-border/50 rounded-xl p-5" data-testid="prune-report-card">
            <h3 className="text-base font-semibold tracking-tight text-foreground flex items-center gap-2 mb-1">
              <Scissors size={16} className="text-primary" /> Bullets Trimmed
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              {localResult.cost_info.prune_report.length} duplicate bullet{localResult.cost_info.prune_report.length === 1 ? "" : "s"} removed to keep your resume tight (turn off "Trim duplicate bullets" before optimizing to skip this).
            </p>
            <ul className="flex flex-col gap-1.5" data-testid="prune-report-list">
              {localResult.cost_info.prune_report.map((line, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground/90">
                  <Scissors size={15} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <TokenUsagePanel usageLog={localResult.cost_info?.usage_log} />
      </div>

      {/* Sticky footer actions */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-background/90 backdrop-blur-xl border-t border-border/40 p-4 lg:px-8" data-testid="results-footer">
        <div className="flex items-center justify-between w-full max-w-7xl mx-auto gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none" data-testid="highlight-toggle">
            <span className="text-foreground font-medium hidden sm:inline">{highlightKeywords ? <Eye size={15} className="inline mr-1" /> : null}Bold keywords in download</span>
            <span className="sm:hidden">Bold</span>
            <button onClick={handleToggleBold} className={`relative w-10 h-5.5 rounded-full transition-colors ${highlightKeywords ? "bg-primary" : "bg-muted-foreground/30"}`} data-testid="highlight-toggle-switch" style={{ height: 22, width: 40 }}>
              <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform" style={{ transform: highlightKeywords ? "translateX(18px)" : "translateX(0)" }} />
            </button>
          </label>
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors" data-testid="preview-btn"><Eye size={15} /> <span className="hidden sm:inline">Preview</span></button>
            <button onClick={() => window.open(`${API}/download-original/${localResult.id}`, "_blank")} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors" data-testid="download-base-btn"><FileText size={15} /> <span className="hidden sm:inline">Base</span></button>
            <button onClick={handleDownload} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 active:scale-[0.98] transition-all" data-testid="download-btn"><Download size={16} /> Download{tracked ? " ✓" : ""}</button>
          </div>
        </div>
      </div>

      {showPreview && (
        <DocxPreview recordId={localResult.id} highlight={highlightKeywords} filename={localResult.original_filename} downloadUrl={downloadUrl} onClose={() => setShowPreview(false)} />
      )}
    </div>
  );
}
